import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Pagination } from '../../components';
import {
  staffActivitiesService,
  staffInvitationsService,
  STAFF_PAGE_SIZE,
} from '../../lib/api-service';
import type { StaffActivity, ActivityInvitation, PaginatedResponse } from '../../lib/types';
import { formatDate } from '../../utils/date';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  RefreshCw,
  Upload,
  XCircle,
} from 'lucide-react';
import { HelpButton } from '../../components/HelpButton';

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  sent: { label: 'Trimis', cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Acceptat', cls: 'bg-green-100 text-green-700' },
  declined: { label: 'Refuzat', cls: 'bg-red-100 text-red-600' },
  expired: { label: 'Expirat', cls: 'bg-gray-100 text-gray-600' },
};

export default function ActivityInvitations() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [activity, setActivity] = useState<StaffActivity | null>(null);
  const [invitations, setInvitations] = useState<ActivityInvitation[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [emailsInput, setEmailsInput] = useState('');
  const [csvInput, setCsvInput] = useState('');
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [act, inv] = await Promise.all([
        staffActivitiesService.detail(slug),
        staffInvitationsService.list(slug, { page }),
      ]);
      setActivity(act);
      setInvitations(inv.results);
      setTotalCount(inv.count);
    } catch {
      setError('Nu s-au putut încărca datele.');
    } finally {
      setLoading(false);
    }
  }, [slug, page]);

  useEffect(() => { load(); }, [load]);

  async function handleSendInvitations() {
    if (!slug) return;
    setSending(true);
    setError('');
    setResult(null);
    try {
      const emails = emailsInput
        .split(/[,;\n]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      const data: { emails?: string[]; csv?: string } = {};
      if (showCsvImport && csvInput) {
        data.csv = csvInput;
      } else {
        data.emails = emails;
      }
      const res = await staffInvitationsService.create(slug, data);
      setResult({ created: res.created_count, skipped: res.skipped_count });
      setEmailsInput('');
      setCsvInput('');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Eroare la trimiterea invitațiilor.');
    } finally {
      setSending(false);
    }
  }

  async function handleResend(id: number) {
    if (!slug) return;
    try {
      await staffInvitationsService.resend(slug, id);
    } catch {
      setError('Eroare la retrimitere.');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  const accepted = invitations.filter((i) => i.status === 'accepted').length;
  const sent = invitations.filter((i) => i.status === 'sent').length;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/staff/activitati')} className="rounded-lg p-2 text-muted-foreground hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Invitații</h1>
            <HelpButton to="/staff/documentatie/invitatii" />
          </div>
          {activity && <p className="mt-0.5 text-sm text-muted-foreground">{activity.title}</p>}
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {result && (
        <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          {result.created} invitații trimise{result.skipped > 0 && `, ${result.skipped} ignorate (deja invitate)`}.
        </div>
      )}

      {/* Stats */}
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5">
          <Mail className="h-4 w-4 text-[var(--vivid-purple)]" />
          <span className="text-sm font-semibold">{totalCount} total</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-2.5">
          <Clock className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-700">{sent} trimise</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-semibold text-green-700">{accepted} acceptate</span>
        </div>
      </div>

      {/* Send invitations */}
      <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Trimite invitații</h2>
          <button
            onClick={() => setShowCsvImport(!showCsvImport)}
            className="flex items-center gap-1 text-xs font-medium text-[var(--vivid-purple)] hover:underline"
          >
            <Upload className="h-3.5 w-3.5" />
            {showCsvImport ? 'Emailuri' : 'Import CSV'}
          </button>
        </div>
        {showCsvImport ? (
          <div>
            <p className="mb-2 text-xs text-muted-foreground">Încarcă un fișier CSV sau lipește conținutul (cu o coloană "email"):</p>
            <div className="mb-3 flex items-center gap-3">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setCsvInput(ev.target?.result as string || '');
                    reader.readAsText(file);
                  }
                }}
                className="text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--vivid-purple)]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[var(--vivid-purple)]"
              />
            </div>
            <textarea
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
              rows={4}
              className="mb-3 w-full rounded-lg border px-3 py-2 text-sm"
              placeholder={'email\nion@example.com\nmaria@example.com'}
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
          </div>
        ) : (
          <div>
            <textarea
              value={emailsInput}
              onChange={(e) => setEmailsInput(e.target.value)}
              rows={3}
              className="mb-3 w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Adrese de email, separate prin virgulă, punct și virgulă sau linie nouă"
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
          </div>
        )}
        <button
          onClick={handleSendInvitations}
          disabled={sending || (!emailsInput.trim() && !csvInput.trim())}
          className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
        >
          <Mail className="h-4 w-4" />
          {sending ? 'Se trimit...' : 'Trimite invitațiile'}
        </button>
      </div>

      {/* Invitations table */}
      {invitations.length === 0 ? (
        <div className="rounded-xl border bg-white py-16 text-center shadow-sm">
          <Mail className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">Nu există invitații pentru această activitate.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/50">
                  <th className="px-4 py-3 text-left font-semibold">Email</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell">Data</th>
                  <th className="px-4 py-3 text-right font-semibold">Acțiuni</th>
                </tr>
              </thead>
              <tbody style={{ fontFamily: 'var(--font-secondary)' }}>
                {invitations.map((inv) => {
                  const statusInfo = STATUS_LABELS[inv.status] || STATUS_LABELS.sent;
                  return (
                    <tr key={inv.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-medium">{inv.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusInfo.cls}`}>
                          {statusInfo.label}
                        </span>
                        {inv.decline_reason && (
                          <p className="mt-0.5 text-xs text-muted-foreground">{inv.decline_reason}</p>
                        )}
                      </td>
                      <td className="hidden whitespace-nowrap px-4 py-3 text-muted-foreground sm:table-cell">
                        {formatDate(inv.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {inv.status === 'sent' && (
                          <button
                            onClick={() => handleResend(inv.id)}
                            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-[var(--vivid-purple)] hover:bg-[var(--vivid-purple)]/5"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}
